import {useContext, useState, useMemo} from "react";
import useCountryAxiosInstance from "../../../hooks/useCountryAxiosInstance";
import { NotificationsContext } from "@ui-components/Notifications";
import {
  PaginationQuotationSchema,
  QuotationCreateResponseSchema,
  QuotationSchema,
  QuotationCreateSchema,
  QuotationWithDraftsSchema,
  QuotationUpdateSchema,
  QuotationUser,
  QuotationDraftCreateSchema,
  QuotationRequestSchema,
} from "../types";
import { useIntl } from "react-intl";
import useAsyncError from "../../../hooks/useAsyncError";
import { Query, QueryResult } from "@material-table/core";
import {
  buildFilterFromQuery,
  MTColumnsType,
  orderingFromQuery,
} from "@utils/pagination";
import {LogoSchema} from "../../profile-logo-editor/types";
import {useLogo} from "@feature/profile-logo-editor/hooks/useLogo";


export const useQuotations = () => {
  // context
  const { push } = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance();
  const throwError = useAsyncError();
  const { getLogo } = useLogo();

  // state
  const [loading, setLoading] = useState(false);
  const [loadingInvitation, setLoadingInvitation]
    = useState<QuotationUser | undefined>();
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingToEnterprise, setSendingToEnterprise] = useState(false)

  const callApi = useMemo(() => async <T>(apiCall: Promise<T>): Promise<T> => {
    setLoading(true);
    try {
      const result = await apiCall;
      setLoading(() => false);
      return result;
    } catch (e) {
      setLoading(false);
      push({
        type: "error",
        title: intl.formatMessage({ id: "server_error" }),
      });
      throwError(e);
    }
    return {} as T;
  }, [intl, push, throwError]);

  const fetchOne = async (id: number): Promise<QuotationWithDraftsSchema> => {
    return countryApiRequest!.get(`/quotations/${id}`);
  };

  const fetch = async (
    query: Query<QuotationSchema>,
    columns: MTColumnsType<QuotationSchema>
  ): Promise<QueryResult<QuotationSchema>> => {
    const payload = {
      filters: (query && buildFilterFromQuery(query, columns)) || null,
      ordering: (query && orderingFromQuery(query)) || null,
      offset: query!.page * query!.pageSize,
      limit: query!.pageSize,
    };
    const response = await callApi(
      countryApiRequest!.post("/quotations/all", payload)
    );
    const { items, total } =
      response as unknown as PaginationQuotationSchema;
    return { data: items, totalCount: total, page: query.page };
  };

  const createQuotation = async (
    payload: QuotationCreateSchema,
  ): Promise<QuotationSchema> => {
    return callApi(countryApiRequest!.post("/quotations", payload));
  };

  const deleteOne = async (id: number): Promise<void> => {
    await callApi(countryApiRequest!.delete(`/quotations/${id}`));
    push({
      type: "info",
      title: intl.formatMessage({ id: "quotation_delete_success" }),
    });
  };

  const invite = async (
    quotationId: number,
    type_: QuotationUser,
  ): Promise<QuotationWithDraftsSchema> => {
    setLoadingInvitation(type_);
    try {
      const result: QuotationWithDraftsSchema = await countryApiRequest!
        .post(`/quotations/invite/${quotationId}`, {type_});
      push({
        type: "info",
        title: intl.formatMessage({
          id: "quotation_invitation_success",
          defaultMessage: "Abilitazione effettuata con successo",
        }),
      });
      return result;
    } catch (e) {
      push({
        type: "error",
        title: intl.formatMessage({id: "server_error"}),
      });
      throwError(e);
      return {} as QuotationWithDraftsSchema;  // Dummy return for typing
    } finally {
      setLoadingInvitation(undefined);
    }
  };

  const updateQuotation = async (
    id: number,
    payload: QuotationUpdateSchema,
    showNotification = true,
  ): Promise<QuotationWithDraftsSchema> => {
    const updatedQuotation: QuotationWithDraftsSchema = 
      await callApi(countryApiRequest!.put(`/quotations/${id}`, payload));
    if (showNotification)
      push({
        type: "success",
        title: intl.formatMessage({
          id: "quotation_update_success",
          defaultMessage: "Preventivo aggiornato con successo",
        }),
      });
    return updatedQuotation;
  }

  const downloadQuotationPdf = async (id:number, draftId: number | undefined): Promise<Blob> => {
    if (!id && !draftId) {
      throw new Error('Either quotation id or draftId must be specified!');
    }
    setDownloadingPdf(true);
    const {user_id, by_resale}: {user_id:number, by_resale:boolean} = await countryApiRequest.post(
      "/quotations/get_creator_info",
      {
        quotation_id: id,
        ...(!!draftId ? {quotation_draft_id: draftId} : {}),
      }
    )
    const logo: LogoSchema = by_resale? await getLogo(
      by_resale ? user_id : undefined
    ): undefined
    const url = `/quotations/pdf`
    const payload = {
      quotation_id: id,
      ...(!!draftId ? {quotation_draft_id: draftId} : {}),
      ...(logo?.url ? {logo_url: logo.url} : {}),
    }
    const r: Blob = await countryApiRequest!.post(
      url,
      payload,
      {responseType: "blob"},
    );
    setDownloadingPdf(false);
    return r;
  }

  const fetchBaseDraftInfo = async (
    quotationId: number,
  ): Promise<{id: number | null, message: string}> => callApi(
    countryApiRequest!.get(`/quotations/${quotationId}/draft/base/info`)
  );

  const sendToEnterprise = async (draftId: number): Promise<void> => {
    setSendingToEnterprise(true)
    await callApi(countryApiRequest!.post(`/quotations/draft/${draftId}/send`));
    setSendingToEnterprise(false)
    push({
      type: "success",
      title: intl.formatMessage({
        id: "quotation_send_to_enterprise_success",
        defaultMessage: "Preventivo inviato con successo",
      }),
    });
  }

  const sendDraftResponse = async (
    draftId: number,
    status: "accepted" | "rejected_hard" | "rejected_soft",
    message: string | undefined,
  ): Promise<void> => {
    /**
     * Send a response to a quotation draft. This method is intended to be called by the enterprise.
     * API call will raise an error otherwise.
     */
    await callApi(
      countryApiRequest!.put(`/quotations/draft/${draftId}/approval`, {status, message })
    );
    push({
      type: "success",
      title: intl.formatMessage({
        id: "quotation_draft_response_success",
        defaultMessage: "Risposta alla bozza inviata con successo",
      }),
    });
  }

  const createQuotationDraft = async (payload: QuotationDraftCreateSchema): Promise<QuotationWithDraftsSchema> => {
    setLoading(true)
    const updated: QuotationWithDraftsSchema = await callApi(countryApiRequest!.post(`/quotations/draft`, payload));
    push({
      type: "success",
      title: intl.formatMessage({
        id: "quotation_draft_create_success",
        defaultMessage: "Bozza preventivo creata con successo",
      }),
    });
    setLoading(false)
    return updated;
  }

  const requestQuotation = async (payload: QuotationRequestSchema): Promise<number> => {
    setLoading(true)
    const status: number = await callApi(countryApiRequest!.post(`/quotations/request`, payload));
    setLoading(false)
    push({
      type: "success",
      title: intl.formatMessage({
        id: "quotation_request_success",
        defaultMessage: "Richiesta preventivo inviata con successo",
      }),
    });
    return status;
  }

  return {
    fetchOne,
    fetch,
    createQuotation,
    updateQuotation,
    deleteOne,
    invite,
    downloadQuotationPdf,
    fetchBaseDraftInfo,
    loading,
    loadingInvitation,
    downloadingPdf,
    sendToEnterprise,
    sendDraftResponse,
    sendingToEnterprise,
    createQuotationDraft,
    requestQuotation,
  };
};
